import React, { useState, useRef } from "react";
import Icon from "./Icon";

const ImageUpload = ({
    label,
    value,
    onChange,
    accept = "image/*",
    maxSize = 10 * 1024 * 1024, // 10MB
    className = "",
}) => {
    const [dragActive, setDragActive] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [previewUrl, setPreviewUrl] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileInput = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    const handleFile = (file) => {
        setError("");

        // Validate file type
        if (!file.type.startsWith("image/")) {
            setError("Please select a valid image file.");
            return;
        }

        // Validate file size
        if (file.size > maxSize) {
            setError(
                `File size must be less than ${Math.round(
                    maxSize / 1024 / 1024
                )}MB.`
            );
            return;
        }

        // Create preview URL for display
        const reader = new FileReader();
        reader.onload = (e) => {
            // Store the file and preview URL
            setSelectedFile(file);
            setPreviewUrl(e.target.result);
            // Call onChange with the file object for upload
            onChange(file);
        };
        reader.onerror = () => {
            setError("Failed to read file.");
        };
        reader.readAsDataURL(file);
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const removeImage = () => {
        onChange(null);
        setSelectedFile(null);
        setPreviewUrl("");
        setError("");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <div className={`image-upload-container ${className}`}>
            {label && <label className="image-upload-label">{label}</label>}

            <div
                className={`image-upload-area ${
                    dragActive ? "drag-active" : ""
                } ${value && (value.image_url || value) ? "has-image" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={triggerFileInput}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFileInput}
                    style={{ display: "none" }}
                />

                {uploading ? (
                    <div className="upload-loading">
                        <div className="upload-spinner"></div>
                        <p>Uploading...</p>
                    </div>
                ) : previewUrl || (value && (value.image_url || value)) ? (
                    <div className="image-preview">
                        <img
                            src={previewUrl || value.image_url || value}
                            alt="Preview"
                        />
                        <div className="image-overlay">
                            <button
                                type="button"
                                className="change-image-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    triggerFileInput();
                                }}
                            >
                                Change
                            </button>
                            <button
                                type="button"
                                className="remove-image-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeImage();
                                }}
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="upload-placeholder">
                        <div className="upload-icon">
                            <Icon name="upload" size="32px" />
                        </div>
                        <p className="upload-text">Drag & drop an image here</p>
                        <p className="upload-subtext">or click to browse</p>
                        <p className="upload-info">
                            Max size: {Math.round(maxSize / 1024 / 1024)}MB
                        </p>
                    </div>
                )}
            </div>

            {error && <div className="upload-error">{error}</div>}
        </div>
    );
};

export default ImageUpload;
